export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categoryName: string;
  coverImage: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
}

export const articles: Article[] = [
  {
    slug: 'cao-deng-he-shang-de-qi-yuan',
    title: '草灯和尚的起源：一盏草灯照千古',
    excerpt: '相传在唐代深山古寺中，一位僧人点燃草灯，以微弱之光照亮经卷，由此开启了草灯和尚的传奇故事。这盏草灯不仅照亮了佛经文字，更照亮了无数修行者心中的智慧之路。',
    content: `<p>相传在唐代的深山古寺中，有一位不知名的僧人，他常年居住在山林之间，以草木为伴，以清泉为饮。一日深夜，寺中油灯熄灭，僧人便取山间枯草，编织成灯，点燃后继续诵读经卷。那微弱的光芒虽然不及油灯明亮，却自有一股清幽之气，与山间的月光、松风相映成趣。</p>
    <p>这就是草灯和尚最初的由来。他以草为灯的故事很快在周边村落传开，人们被这位僧人朴素而坚韧的修行精神所感动。草灯和尚不仅是一个具体的形象，更是一种精神的象征——在最简陋的条件下，依然能够找到内心的光明。</p>
    <p>随着时间推移，草灯和尚的形象逐渐丰富起来。他手持草灯、身着朴素僧袍的形象，成为了禅宗文化中一个独特的符号。那盏草灯，象征着每个人心中本具的智慧之光，只需点燃，便能照亮前行的道路。</p>
    <p>在禅宗的语境中，草灯和尚的故事还蕴含着深刻的寓意：真正的光明不在于外在的华丽与富足，而在于内心的觉悟与坚持。一草一木，皆可为灯；一心一念，皆可成佛。</p>`,
    category: 'chan-yi-gu-shi',
    categoryName: '禅意故事',
    coverImage: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20temple%20in%20misty%20mountains%2C%20a%20single%20grass%20lamp%20glowing%20softly%20on%20a%20wooden%20desk%2C%20warm%20amber%20light%2C%20zen%20atmosphere%2C%20ink%20wash%20painting%20style%2C%20serene%20and%20peaceful%2C%20traditional%20Chinese%20aesthetic%2C%20soft%20natural%20lighting%2C%20minimalist%20composition&width=800&height=500&seq=article-cover-01&orientation=landscape',
    author: '草灯文化研究院',
    date: '2024-12-15',
    readTime: '8分钟',
    tags: ['草灯和尚', '禅宗起源', '唐代文化', '修行故事'],
  },
  {
    slug: 'yi-cao-wei-deng-chan-yi-ren-sheng',
    title: '以草为灯：禅意人生的三重境界',
    excerpt: '草灯和尚的修行哲学中，蕴含着"以草为灯"的三重境界——从外在的物质简朴，到内心的精神丰盈，最终抵达物我两忘的自在之境。',
    content: `<p>草灯和尚的修行哲学，可以概括为"以草为灯"的三重境界。这三重境界层层递进，既是个人的修行之路，也是每个人都可以参考的人生智慧。</p>
    <p><strong>第一重境界：见草是草</strong></p>
    <p>初入修行之人，看到的是物质的简朴。草就是草，灯就是灯。在这个阶段，修行者学会放下对外在物质的执着，接受简朴的生活方式。正如草灯和尚用最普通的草木编织成灯，不求华丽，只求实用。这是修行的起点——懂得简朴的价值。</p>
    <p><strong>第二重境界：见草不是草</strong></p>
    <p>当修行深入，草不再仅仅是草，灯也不再仅仅是灯。草灯成为了内心的象征，那微弱的光芒代表着本具的佛性。修行者开始向内探索，在简朴的外在条件下，培育丰盈的内心世界。一盏草灯，足以照亮整个心灵的空间。</p>
    <p><strong>第三重境界：见草还是草</strong></p>
    <p>修行的最高境界，是回归本真。草还是草，灯还是灯，但修行者的心境已经完全不同了。不再刻意区分简朴与丰盈、外在和内在，一切回归自然。这正是禅宗所说的"平常心是道"——在最平常的事物中，见到最深刻的真理。</p>`,
    category: 'xiu-xing-zhe-xue',
    categoryName: '修行哲学',
    coverImage: 'https://readdy.ai/api/search-image?query=Minimalist%20zen%20scene%20with%20a%20handwoven%20grass%20lamp%20emitting%20soft%20golden%20glow%2C%20surrounded%20by%20dried%20grass%20and%20simple%20ceramic%20bowl%2C%20warm%20earthy%20tones%2C%20Japanese%20wabi-sabi%20aesthetic%2C%20soft%20natural%20window%20light%2C%20peaceful%20meditation%20space&width=800&height=500&seq=article-cover-02&orientation=landscape',
    author: '明心居士',
    date: '2025-01-20',
    readTime: '12分钟',
    tags: ['草灯和尚', '修行哲学', '三重境界', '禅宗智慧'],
  },
  {
    slug: 'cao-mu-you-qing-zi-ran-zhi-dao',
    title: '草木有情：草灯和尚的自然之道',
    excerpt: '草灯和尚与草木建立了深厚的情感联系。在他的世界里，每一株草木都有灵性，每一片落叶都在说法。这种对自然的敬畏与亲近，正是东方生态智慧的源头。',
    content: `<p>草灯和尚之所以以"草"为名，正是因为他与草木之间有着不解之缘。在禅宗的传统中，自然界的一切都是佛法的显现。山河大地、草木丛林，无不在宣说着宇宙的真理。</p>
    <p>草灯和尚常年居住在山林之中，他以草木为师，以自然为友。春天，他观察新芽破土而出的坚韧；夏天，他感受草木繁茂的生命力；秋天，他领悟落叶归根的从容；冬天，他体会枯木待春的希望。</p>
    <p>这种对自然的深刻体悟，形成了草灯和尚独特的生态智慧。他认为，人类不是自然的主宰者，而是自然的一部分。当我们用敬畏之心对待每一株草木时，我们也在敬畏自己内在那份纯净的本性。</p>
    <p>在现代社会，草灯和尚的自然之道显得尤为珍贵。在快节奏的都市生活中，我们常常忘记了与自然的连接。而草灯和尚的故事提醒我们：回归自然，就是回归本心。</p>`,
    category: 'sheng-huo-mei-xue',
    categoryName: '生活美学',
    coverImage: 'https://readdy.ai/api/search-image?query=Serene%20bamboo%20forest%20with%20soft%20morning%20mist%2C%20a%20simple%20stone%20path%20leading%20through%2C%20dew%20drops%20on%20green%20leaves%2C%20traditional%20Chinese%20ink%20painting%20aesthetic%2C%20ethereal%20atmosphere%2C%20soft%20natural%20light%20filtering%20through%20bamboo%20leaves%2C%20zen%20garden%20vibe&width=800&height=500&seq=article-cover-03&orientation=landscape',
    author: '草灯文化研究院',
    date: '2025-02-08',
    readTime: '10分钟',
    tags: ['草灯和尚', '自然之道', '草木灵性', '生态智慧'],
  },
  {
    slug: 'cao-deng-he-shang-yi-shu-xing-xiang',
    title: '笔墨之间：草灯和尚的艺术形象演变',
    excerpt: '从古代壁画到当代水墨，从戏曲舞台到现代设计，草灯和尚的艺术形象经历了丰富的演变。每一个时代的艺术家都在用自己的方式重新诠释这位禅意人物。',
    content: `<p>草灯和尚的艺术形象，是中国传统人物画中一个独特而富有魅力的主题。从唐代的寺庙壁画开始，到宋元明清的文人画，再到当代的各种艺术形式，草灯和尚的形象不断被重新塑造和诠释。</p>
    <p>在早期的艺术表现中，草灯和尚通常被描绘为一位清瘦的老僧，手持草灯，面带慈悲微笑。这种形象强调的是他的智慧与慈悲，草灯作为核心符号，象征着智慧之光的传递。</p>
    <p>到了宋元时期，文人画家们开始赋予草灯和尚更多的文人气质。画中的他往往置身于山水之间，草灯成为了人与自然和谐共处的媒介。这些作品充满了诗意和哲思，将禅宗的空灵与文人的雅致完美结合。</p>
    <p>在当代，草灯和尚的艺术形象更加多元化。有的艺术家将他与现代都市背景结合，创造出一种传统与现代碰撞的张力；有的设计师将草灯元素融入产品设计，让禅意美学走进日常生活。</p>`,
    category: 'yi-shu-xing-xiang',
    categoryName: '艺术形象',
    coverImage: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20wash%20painting%20of%20an%20elderly%20monk%20holding%20a%20simple%20grass%20lantern%2C%20standing%20in%20misty%20mountains%2C%20flowing%20robes%2C%20serene%20expression%2C%20elegant%20brushstrokes%2C%20rice%20paper%20texture%2C%20classical%20Chinese%20art%20style&width=800&height=500&seq=article-cover-04&orientation=landscape',
    author: '艺禅居士',
    date: '2025-03-12',
    readTime: '11分钟',
    tags: ['草灯和尚', '艺术形象', '水墨画', '传统文化'],
  },
  {
    slug: 'xian-dai-she-hui-zhong-de-chan-yi',
    title: '现代生活中的草灯智慧：如何在都市中修行',
    excerpt: '在钢筋水泥的都市丛林中，草灯和尚的智慧依然熠熠生辉。本文探讨如何将"以草为灯"的禅意融入现代日常生活，在喧嚣中找到内心的宁静。',
    content: `<p>现代都市生活充满了压力和焦虑，但草灯和尚的智慧告诉我们：修行的道场不只在深山古寺，更在日常生活的每一个瞬间。</p>
    <p>晨起一杯清茶，可以是修行；地铁上的一次深呼吸，可以是修行；工作中的专注投入，也可以是修行。草灯和尚的核心精神——"以草为灯、以心照路"——在现代社会中依然适用。</p>
    <p>具体来说，我们可以从以下几个方面实践草灯智慧：<br/>一、简化物质生活，减少不必要的消费和占有；<br/>二、培养专注力，学会在每一件事上全身心投入；<br/>三、与自然保持联系，哪怕只是在阳台上种一盆绿植；<br/>四、练习感恩之心，珍惜身边的一草一木、一人一事。</p>`,
    category: 'xian-dai-chuan-cheng',
    categoryName: '现代传承',
    coverImage: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20apartment%20with%20zen%20elements%2C%20a%20small%20grass%20lamp%20on%20a%20wooden%20shelf%2C%20morning%20sunlight%20streaming%20through%20window%2C%20simple%20tea%20set%2C%20potted%20bamboo%20plant%2C%20peaceful%20urban%20living%2C%20warm%20natural%20tones%2C%20cozy%20atmosphere&width=800&height=500&seq=article-cover-05&orientation=landscape',
    author: '都市禅修者',
    date: '2025-04-05',
    readTime: '9分钟',
    tags: ['草灯和尚', '现代生活', '都市修行', '禅意生活'],
  },
  {
    slug: 'cao-deng-he-shang-yu-chan-zong',
    title: '草灯和尚与禅宗：一盏灯照亮千年法脉',
    excerpt: '草灯和尚作为禅宗文化的重要符号，承载着禅宗"直指人心、见性成佛"的核心教义。探究草灯和尚与禅宗思想的深层关联。',
    content: `<p>禅宗是中国佛教最具影响力的宗派之一，其核心教义是"不立文字，教外别传，直指人心，见性成佛"。草灯和尚的形象与禅宗的这一精神高度契合。</p>
    <p>草灯和尚手中的草灯，正是禅宗"明心见性"的绝佳隐喻。禅宗认为，每个人心中都有一盏本具的明灯，只是被尘世的烦恼所遮蔽。修行的目的，就是擦亮这盏心灯，让它重新发出光明。</p>
    <p>在禅宗的历史上，有许多与灯有关的故事和公案。比如"传灯"一词，就是禅宗法脉传承的重要象征。草灯和尚的草灯，虽然不是华丽的琉璃灯，却同样承载着智慧传承的使命。</p>`,
    category: 'xiu-xing-zhe-xue',
    categoryName: '修行哲学',
    coverImage: 'https://readdy.ai/api/search-image?query=Ancient%20zen%20temple%20interior%20with%20stone%20walls%20and%20wooden%20beams%2C%20a%20single%20oil%20lamp%20casting%20warm%20glow%2C%20incense%20smoke%20curling%20upward%2C%20Buddhist%20statue%20in%20shadow%2C%20deep%20spiritual%20atmosphere%2C%20traditional%20Chinese%20temple%20architecture&width=800&height=500&seq=article-cover-06&orientation=landscape',
    author: '明心居士',
    date: '2025-05-18',
    readTime: '13分钟',
    tags: ['草灯和尚', '禅宗', '法脉传承', '佛教文化'],
  },
  {
    slug: 'dong-fang-sheng-huo-mei-xue-zhi-lu',
    title: '东方生活美学之路：从草灯到日常',
    excerpt: '草灯和尚所代表的生活美学，是一种化繁为简、返璞归真的东方智慧。本文从茶道、花道、香道等多个维度，探索草灯和尚的生活美学如何影响我们的日常。',
    content: `<p>东方生活美学，讲究的是"道器合一"——在最日常的器物中，见到最高深的道理。草灯和尚手中的那盏草灯，正是这种美学的完美体现。</p>
    <p>在茶道中，一碗清茶可以是整个宇宙；在花道中，一枝野花可以蕴含四季流转；在香道中，一缕轻烟可以连接天地。这些看似简单的日常行为，在东方美学的视角下，都成为了修行的方式。</p>
    <p>草灯和尚的生活美学教给我们：美不在于奢华，而在于用心；不在于繁复，而在于简约。当我们用"草灯"的眼光去看待生活时，每一个平凡的瞬间都变得诗意盎然。</p>`,
    category: 'sheng-huo-mei-xue',
    categoryName: '生活美学',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20tea%20ceremony%20setup%20with%20clay%20teapot%20and%20small%20cups%20on%20wooden%20tray%2C%20a%20single%20wildflower%20in%20small%20vase%2C%20soft%20natural%20light%2C%20warm%20earthy%20colors%2C%20traditional%20Chinese%20aesthetic%2C%20peaceful%20morning%20scene&width=800&height=500&seq=article-cover-07&orientation=landscape',
    author: '草灯文化研究院',
    date: '2025-06-22',
    readTime: '10分钟',
    tags: ['草灯和尚', '生活美学', '茶道', '东方文化'],
  },
  {
    slug: 'cao-deng-he-shang-xiu-xing-fa-men',
    title: '草灯和尚的修行法门：静坐与观心',
    excerpt: '草灯和尚的修行方法以"静坐观心"为核心，通过简单的坐禅和观想练习，帮助修行者回归内心的平静与清明。',
    content: `<p>草灯和尚的修行法门，以"静坐"和"观心"为两大核心。这两个方法看似简单，却是禅宗修行最根本的途径。</p>
    <p><strong>静坐之法</strong><br/>草灯和尚教导的静坐，不需要特定的姿势或环境。无论是在山林间，还是在屋檐下，只要能够安静下来，就可以开始。关键不在于身体的姿势，而在于心的安住。就像那盏草灯，无论放在哪里，它的光芒都不增不减。</p>
    <p><strong>观心之法</strong><br/>静坐之后，便是观心。观心不是用力去思考或分析，而是像观察一盏灯的光芒一样，只是静静地看着自己的念头来来去去。不追逐、不抗拒、不评判，只是觉察。</p>`,
    category: 'xiu-xing-zhe-xue',
    categoryName: '修行哲学',
    coverImage: 'https://readdy.ai/api/search-image?query=Simple%20meditation%20cushion%20on%20wooden%20floor%20in%20quiet%20room%2C%20soft%20morning%20light%20through%20paper%20window%2C%20a%20single%20incense%20stick%20burning%2C%20minimalist%20zen%20interior%2C%20peaceful%20atmosphere%2C%20warm%20natural%20tones%2C%20Japanese%20wabi-sabi%20style&width=800&height=500&seq=article-cover-08&orientation=landscape',
    author: '明心居士',
    date: '2025-07-10',
    readTime: '11分钟',
    tags: ['草灯和尚', '修行法门', '静坐', '观心'],
  },
];

export const featuredArticleSlugs = [
  'cao-deng-he-shang-de-qi-yuan',
  'yi-cao-wei-deng-chan-yi-ren-sheng',
  'cao-mu-you-qing-zi-ran-zhi-dao',
];

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug);
}

export function getArticlesByCategory(categorySlug: string): Article[] {
  return articles.filter((a) => a.category === categorySlug);
}

export function getRelatedArticles(currentSlug: string, limit: number = 3): Article[] {
  const current = getArticleBySlug(currentSlug);
  if (!current) return [];
  return articles
    .filter((a) => a.slug !== currentSlug && a.category === current.category)
    .slice(0, limit);
}